import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { clubInfo } from "@/lib/mbti";
import PersonaVariants from "@/components/PersonaVariants";
import EmailPreview from "@/components/EmailPreview";
import CommuniqueAiHelper from "@/components/CommuniqueAiHelper";
import { Megaphone, Upload, Trash2, Send, Music, FileText, Eye, CalendarClock } from "lucide-react";

const CLUBS = ["Diplomatas", "Analistas", "Sentinelas", "Exploradores"];
const GROUPS = [["competing", "Coopetidores"], ["leaders", "Secretários e Presidentes"], ["observers", "Observadores"]];
const EMPTY = { subject: "", message: "", clubs: [...CLUBS], groups: ["competing"], files: [], send_at: "", variants: null, leader_message: "", remind: true };

const fmt = (iso) => new Date(iso).toLocaleString("pt-PT", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
const groupLabel = (g) => GROUPS.find(([k]) => k === g)?.[1] || g;

const Opens = ({ list }) => {
  const keys = [...new Set(list.map((r) => `${r.club}|${r.group}`))].sort();
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-testid="communique-opens">
      {keys.map((k) => {
        const [club, group] = k.split("|");
        const rs = list.filter((r) => r.club === club && r.group === group);
        const opened = rs.filter((r) => r.opened_at).length;
        return (
          <div key={k} className="bg-[#0F0F0F] border border-[#1F1F1F] p-3">
            <div className="flex justify-between text-[10px] uppercase tracking-widest font-bold mb-2">
              <span style={{ color: clubInfo(club)?.color }}>{club} · {groupLabel(group)}</span>
              <span className="text-[#A3A3A3]">{opened}/{rs.length} abriram</span>
            </div>
            {rs.map((r) => (
              <div key={r.id} className="flex justify-between text-xs py-0.5">
                <span className={r.opened_at ? "text-white" : "text-[#555]"}>{r.name}</span>
                <span className={r.opened_at ? "text-[#22C55E]" : "text-[#555]"}>{r.opened_at ? fmt(r.opened_at) : "por abrir"}</span>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

const History = ({ rows, onCancel }) => {
  const [open, setOpen] = useState(null);
  return (
    <div className="space-y-2" data-testid="communique-history">
      <div className="kw-overline">Enviados e agendados</div>
      {rows.length === 0 && <p className="text-xs text-[#555]">Ainda nenhum comunicado.</p>}
      {rows.map((c) => (
        <div key={c.id} className="kw-card px-5 py-4" data-testid={`communique-${c.id}`}>
          <div className="flex flex-wrap items-center gap-3">
            {c.status === "scheduled"
              ? <span className="text-[10px] uppercase tracking-widest font-bold text-[#EAB308] inline-flex items-center gap-1"><CalendarClock className="w-3 h-3" /> Agendado {fmt(c.send_at)}</span>
              : <span className="text-[10px] uppercase tracking-widest font-bold text-[#22C55E]">Enviado {c.sent_at ? fmt(c.sent_at) : ""}</span>}
            <span className="font-bold flex-1">{c.subject}</span>
            {c.status === "sent" && (
              <button onClick={() => setOpen(open === c.id ? null : c.id)} data-testid={`communique-opens-${c.id}`}
                className="text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 border border-[#333] text-[#A3A3A3] hover:text-white inline-flex items-center gap-1.5">
                <Eye className="w-3 h-3" /> {c.opened}/{c.recipients} abriram
              </button>
            )}
            {c.status === "scheduled" && (
              <button onClick={() => onCancel(c)} data-testid={`communique-cancel-${c.id}`}
                className="text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 border border-[#333] text-[#A3A3A3] hover:text-[#E50000]">
                Cancelar
              </button>
            )}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-1">
            {c.clubs.join(", ")} · {(c.groups || ["competing"]).map(groupLabel).join(" + ")}
            {c.reminder_sent_at ? ` · lembrete enviado a ${c.reminder_count ?? 0}` : c.remind ? " · lembrete aos 3 dias" : ""} · {c.study_files.length} ficheiros · {c.sent_by}
          </div>
          {open === c.id && <Opens list={c.recipient_list || []} />}
        </div>
      ))}
    </div>
  );
};

export default function Communiques() {
  const [form, setForm] = useState(EMPTY);
  const [counts, setCounts] = useState({});
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/communiques").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/communiques/audience").then((r) => setCounts(r.data)).catch(() => {});
  }, []);

  const count = (clubs, groups) => clubs.reduce((s, c) => s + groups.reduce((t, g) => t + (counts[c]?.[g] || 0), 0), 0);
  const total = count(form.clubs, form.groups);
  const flip = (key, v) => setForm((f) => ({ ...f, [key]: f[key].includes(v) ? f[key].filter((x) => x !== v) : [...f[key], v] }));
  const toggle = (c) => flip("clubs", c);

  const upload = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    setBusy("upload");
    for (const file of list) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const r = await api.post("/uploads/material", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => ({ ...f, files: [...f.files, { token: r.data.token, filename: r.data.filename, kind: r.data.kind }] }));
      } catch (err) { toast.error(`${file.name}: ${formatApiError(err)}`); }
    }
    setBusy(null);
  };

  const send = async () => {
    const when = form.send_at ? ` em ${fmt(form.send_at)}` : " agora";
    if (!window.confirm(`Enviar "${form.subject}" a ${total} pessoas${when}?`)) return;
    setBusy("send");
    try {
      const r = await api.post("/communiques", { ...form, send_at: form.send_at || null });
      toast.success(r.data.scheduled ? `Comunicado agendado para ${fmt(r.data.send_at)}` : `Comunicado enviado · ${r.data.emails} de ${r.data.recipients} emails`);
      setForm(EMPTY);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(null);
  };

  const ready = form.subject.trim().length >= 3 && form.message.trim().length >= 5 && total > 0;

  return (
    <div className="space-y-8" data-testid="communiques-page">
      <div>
        <div className="kw-overline mb-2 flex items-center gap-2"><Megaphone className="w-3.5 h-3.5" /> Comunicação</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight">
          Comuni<span className="text-[#E50000]">cados</span>
        </h1>
        <p className="text-sm text-[#A3A3A3] mt-3 max-w-2xl">
          Envia documentos e áudios por email aos coopetidores, secretários, presidentes e observadores de cada clube. Os ficheiros ficam também na Biblioteca de Estudo.
        </p>
      </div>

      <div className="kw-card p-6 space-y-5 max-w-3xl">
        <div>
          <label className="kw-overline text-[10px] block mb-2">Quem recebe</label>
          <div className="flex flex-wrap gap-2 mb-4">
            {GROUPS.map(([g, label]) => {
              const on = form.groups.includes(g);
              return (
                <button key={g} onClick={() => flip("groups", g)} data-testid={`communique-group-${g}`}
                  className={`text-[10px] uppercase tracking-widest font-bold px-3 py-2 border transition-colors ${on ? "border-[#E50000] text-white bg-[#E50000]/15" : "border-[#333] text-[#737373]"}`}>
                  {label} ({count(form.clubs, [g])})
                </button>
              );
            })}
          </div>
          <label className="kw-overline text-[10px] block mb-2">Clubes</label>
          <div className="flex flex-wrap gap-2">
            {CLUBS.map((c) => {
              const on = form.clubs.includes(c);
              const color = clubInfo(c)?.color || "#E50000";
              return (
                <button key={c} onClick={() => toggle(c)} data-testid={`communique-club-${c}`}
                  className="text-[10px] uppercase tracking-widest font-bold px-3 py-2 border transition-colors"
                  style={on ? { borderColor: color, color, background: `${color}14` } : { borderColor: "#333", color: "#737373" }}>
                  {c} ({count([c], form.groups)})
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <label className="kw-overline text-[10px] block mb-1">Assunto</label>
          <input className="kw-input" value={form.subject} maxLength={150} data-testid="communique-subject"
            onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Ex.: Guião da visita de avaliação" />
        </div>
        <div>
          <label className="kw-overline text-[10px] block mb-1">Mensagem</label>
          <textarea className="kw-input min-h-[140px]" value={form.message} maxLength={5000} data-testid="communique-message"
            onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Escreve a mensagem base. Cada email começa com &quot;Olá&quot; e o primeiro nome da pessoa." />
        </div>
        {form.groups.includes("leaders") && (
          <div data-testid="communique-leader-block">
            <label className="kw-overline text-[10px] block mb-1">Texto para a liderança (Secretário e Presidente)</label>
            <textarea className="kw-input min-h-[100px]" value={form.leader_message || ""} maxLength={5000}
              data-testid="communique-leader-message"
              onChange={(e) => setForm({ ...form, leader_message: e.target.value })}
              placeholder="Opcional. Ex.: És tu que coordenas estas eleições — combina com o grupo e avança assim que possível." />
            <p className="text-[11px] text-[#737373] mt-1">
              Se escreveres aqui, o Secretário e o Presidente recebem este texto em vez da mensagem base. Em branco, recebem a mensagem base.
            </p>
          </div>
        )}
        <CommuniqueAiHelper form={form} setForm={setForm} />
        <PersonaVariants form={form} setForm={setForm} />
        <div className="space-y-1.5" data-testid="communique-files">
          {form.files.map((f) => (
            <div key={f.token} className="flex items-center gap-2 text-sm bg-[#0F0F0F] border border-[#1F1F1F] px-3 py-2">
              {f.kind === "audio" ? <Music className="w-4 h-4 text-[#E50000]" /> : <FileText className="w-4 h-4 text-[#A3A3A3]" />}
              <span className="flex-1 truncate">{f.filename}</span>
              <button onClick={() => setForm({ ...form, files: form.files.filter((x) => x.token !== f.token) })}
                className="text-[#555] hover:text-[#E50000]" data-testid={`communique-remove-${f.token}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="kw-btn-secondary !py-2 text-[10px] inline-flex items-center gap-1.5 cursor-pointer">
            <Upload className="w-3 h-3" /> {busy === "upload" ? "A carregar…" : "Anexar ficheiros"}
            <input type="file" multiple accept=".pdf,.pptx,.docx,.png,.jpg,.jpeg,.mp3,.m4a,.wav,.ogg,.aac" className="hidden"
              onChange={upload} disabled={!!busy} data-testid="communique-upload" />
          </label>
          <label className="flex items-center gap-2 text-xs text-[#A3A3A3] cursor-pointer">
            <input type="checkbox" checked={form.remind} onChange={(e) => setForm({ ...form, remind: e.target.checked })}
              className="accent-[#E50000]" data-testid="communique-remind" />
            Reenviar a quem não abrir ao fim de 3 dias
          </label>
          <div className="flex items-center gap-2">
            <label className="kw-overline text-[10px]">Agendar (opcional)</label>
            <input type="datetime-local" className="kw-input !py-1.5 !w-auto" value={form.send_at}
              onChange={(e) => setForm({ ...form, send_at: e.target.value })} data-testid="communique-send-at" />
          </div>
          <EmailPreview form={form} />
          <button onClick={send} disabled={!ready || !!busy} className="kw-btn-primary !py-2 text-[10px] inline-flex items-center gap-1.5"
            data-testid="communique-send">
            <Send className="w-3 h-3" /> {busy === "send" ? "A enviar…" : `${form.send_at ? "Agendar para" : "Enviar a"} ${total} pessoas`}
          </button>
        </div>
        <p className="text-[10px] text-[#737373]">PDFs seguem em anexo e em link (até ~18 MB no total em anexo); o áudio abre com o botão "Ouvir". PDF até 20 MB, áudio até 80 MB.</p>
      </div>

      <History rows={rows} onCancel={async (c) => {
        if (!window.confirm(`Cancelar o envio agendado de "${c.subject}"?`)) return;
        try { await api.delete(`/communiques/${c.id}`); toast.success("Envio cancelado"); load(); } catch (err) { toast.error(formatApiError(err)); }
      }} />
    </div>
  );
}
